/**
 * Base Service Class
 * 
 * Provides a foundation for all service classes with:
 * - Input validation
 * - Business logic separation
 * - Transaction handling
 * - Event emitting
 * - Error transformation
 * 
 * Services sit between the UI and repositories, handling:
 * 1. Input validation before database operations
 * 2. Complex business logic that spans multiple repositories
 * 3. Data transformation and enrichment
 * 4. Transaction coordination
 * 5. Event publishing for integrations
 * 
 * @example
 * ```typescript
 * class CustomerService extends BaseService {
 *   async createCustomer(data: CreateCustomerInput): Promise<Customer> {
 *     // Validate input
 *     this.validate(data, customerSchema);
 *     
 *     // Business logic
 *     const normalized = this.normalizeCustomerData(data);
 *     
 *     // Repository call
 *     const customer = await repositories.customers.create(normalized);
 *     
 *     // Emit event
 *     this.emit('customer.created', customer);
 *     
 *     return customer;
 *   }
 * }
 * ```
 */

import { RepositoryError } from '../repositories/BaseRepository';

/**
 * Validation schema type
 */
export interface ValidationSchema {
  [key: string]: ValidationRule;
}

/**
 * Validation rule definition
 */
export interface ValidationRule {
  required?: boolean;
  type?: 'string' | 'number' | 'boolean' | 'object' | 'array' | 'email' | 'phone' | 'date';
  min?: number;
  max?: number;
  pattern?: RegExp;
  custom?: (value: any) => boolean | string;
  message?: string;
}

/**
 * Validation error details
 */
export interface ValidationError {
  field: string;
  message: string;
  value?: any;
}

/**
 * Service error class
 */
export class ServiceError extends Error {
  public readonly code: string;
  public readonly statusCode: number;
  public readonly details?: any;
  public readonly originalError?: any;

  constructor(
    message: string,
    code: string = 'SERVICE_ERROR',
    statusCode: number = 500,
    details?: any,
    originalError?: any
  ) {
    super(message);
    this.name = 'ServiceError';
    this.code = code;
    this.statusCode = statusCode;
    this.details = details;
    this.originalError = originalError;

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ServiceError);
    }
  }

  /**
   * Create a validation error
   */
  static validation(errors: ValidationError[]): ServiceError {
    return new ServiceError(
      'Validation failed',
      'VALIDATION_ERROR',
      400,
      { errors }
    );
  }

  /**
   * Create a not found error
   */
  static notFound(resource: string, id?: string): ServiceError {
    return new ServiceError(
      `${resource} not found${id ? `: ${id}` : ''}`,
      'NOT_FOUND',
      404
    );
  }

  /**
   * Create an unauthorized error
   */
  static unauthorized(message: string = 'Unauthorized'): ServiceError {
    return new ServiceError(message, 'UNAUTHORIZED', 401);
  }

  /**
   * Create a forbidden error
   */
  static forbidden(message: string = 'Forbidden'): ServiceError {
    return new ServiceError(message, 'FORBIDDEN', 403);
  }

  /**
   * Create a conflict error
   */
  static conflict(message: string): ServiceError {
    return new ServiceError(message, 'CONFLICT', 409);
  }

  /**
   * Transform a repository error into a service error
   */
  static fromRepositoryError(error: RepositoryError): ServiceError {
    if (error.isNotFound()) {
      return ServiceError.notFound('Resource', error.message);
    }
    
    if (error.isPermissionDenied()) {
      return ServiceError.forbidden('Permission denied');
    }
    
    if (error.isConstraintViolation()) {
      return ServiceError.conflict('Operation violates database constraints');
    }

    return new ServiceError(
      error.message,
      'DATABASE_ERROR',
      500,
      undefined,
      error
    );
  }
}

/**
 * Event emitter type
 */
export type EventHandler = (data: any) => void | Promise<void>;

/**
 * Base Service Class
 * 
 * All service classes should extend this base class to get
 * validation, error handling, and event emitting capabilities.
 */
export abstract class BaseService {
  private eventHandlers: Map<string, EventHandler[]> = new Map();

  /**
   * Validate data against a schema
   * 
   * @param data Data to validate
   * @param schema Validation schema
   * @throws ServiceError if validation fails
   */
  protected validate(data: any, schema: ValidationSchema): void {
    const errors: ValidationError[] = [];

    for (const [field, rule] of Object.entries(schema)) {
      const value = data[field];

      // Required check
      if (rule.required && (value === undefined || value === null || value === '')) {
        errors.push({
          field,
          message: rule.message || `${field} is required`,
          value
        });
        continue;
      }

      // Skip further validation if not required and empty
      if (!rule.required && (value === undefined || value === null || value === '')) {
        continue;
      }

      // Type validation
      if (rule.type) {
        if (!this.validateType(value, rule.type)) {
          errors.push({
            field,
            message: rule.message || `${field} must be of type ${rule.type}`,
            value
          });
          continue;
        }
      }

      // Min/Max validation
      if (rule.min !== undefined) {
        if (typeof value === 'number' && value < rule.min) {
          errors.push({
            field,
            message: rule.message || `${field} must be at least ${rule.min}`,
            value
          });
        } else if (typeof value === 'string' && value.length < rule.min) {
          errors.push({
            field,
            message: rule.message || `${field} must be at least ${rule.min} characters`,
            value
          });
        }
      }

      if (rule.max !== undefined) {
        if (typeof value === 'number' && value > rule.max) {
          errors.push({
            field,
            message: rule.message || `${field} must be at most ${rule.max}`,
            value
          });
        } else if (typeof value === 'string' && value.length > rule.max) {
          errors.push({
            field,
            message: rule.message || `${field} must be at most ${rule.max} characters`,
            value
          });
        }
      }

      // Pattern validation
      if (rule.pattern && typeof value === 'string') {
        if (!rule.pattern.test(value)) {
          errors.push({
            field,
            message: rule.message || `${field} format is invalid`,
            value
          });
        }
      }

      // Custom validation
      if (rule.custom) {
        const result = rule.custom(value);
        if (result !== true) {
          errors.push({
            field,
            message: typeof result === 'string' ? result : (rule.message || `${field} is invalid`),
            value
          });
        }
      }
    }

    if (errors.length > 0) {
      throw ServiceError.validation(errors);
    }
  }

  /**
   * Validate value type
   */
  private validateType(value: any, type: ValidationRule['type']): boolean {
    switch (type) {
      case 'string':
        return typeof value === 'string';
      case 'number':
        return typeof value === 'number' && !isNaN(value);
      case 'boolean':
        return typeof value === 'boolean';
      case 'object':
        return typeof value === 'object' && value !== null && !Array.isArray(value);
      case 'array':
        return Array.isArray(value);
      case 'email':
        return typeof value === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
      case 'phone':
        return typeof value === 'string' && /^\+?[\d\s\-\(\)]+$/.test(value);
      case 'date':
        return !isNaN(Date.parse(value));
      default:
        return true;
    }
  }

  /**
   * Emit an event
   * 
   * @param event Event name
   * @param data Event data
   */
  protected async emit(event: string, data: any): Promise<void> {
    const handlers = this.eventHandlers.get(event) || [];
    
    for (const handler of handlers) {
      try {
        await handler(data);
      } catch (error) {
        console.error(`Error in event handler for ${event}:`, error);
      }
    }
  }

  /**
   * Register an event handler
   * 
   * @param event Event name
   * @param handler Event handler function
   */
  public on(event: string, handler: EventHandler): void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, []);
    }
    this.eventHandlers.get(event)!.push(handler);
  }

  /**
   * Remove an event handler
   * 
   * @param event Event name
   * @param handler Event handler function
   */
  public off(event: string, handler: EventHandler): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  /**
   * Handle repository errors and transform to service errors
   */
  protected handleError(error: any): never {
    if (error instanceof ServiceError) {
      throw error;
    }

    if (error instanceof RepositoryError) {
      throw ServiceError.fromRepositoryError(error);
    }

    throw new ServiceError(
      error.message || 'An unexpected error occurred',
      'INTERNAL_ERROR',
      500,
      undefined,
      error
    );
  }

  /**
   * Validate required fields
   */
  protected requireFields(data: any, fields: string[]): void {
    const missing = fields.filter(field => !data[field]);
    
    if (missing.length > 0) {
      throw ServiceError.validation(
        missing.map(field => ({
          field,
          message: `${field} is required`
        }))
      );
    }
  }

  /**
   * Sanitize string input (trim, remove extra spaces)
   */
  protected sanitizeString(value: string | null | undefined): string | null {
    if (!value) return null;
    return value.trim().replace(/\s+/g, ' ');
  }

  /**
   * Normalize phone number format
   */
  protected normalizePhone(phone: string | null | undefined): string | null {
    if (!phone) return null;
    return phone.replace(/\D/g, '');
  }

  /**
   * Normalize email (lowercase, trim)
   */
  protected normalizeEmail(email: string | null | undefined): string | null {
    if (!email) return null;
    return email.toLowerCase().trim();
  }

  /**
   * Parse date string safely
   */
  protected parseDate(dateString: string | null | undefined): Date | null {
    if (!dateString) return null;
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? null : date;
  }
}

/**
 * Common validation schemas
 */
export const CommonSchemas = {
  email: {
    type: 'email' as const,
    message: 'Invalid email address'
  },
  
  phone: {
    type: 'phone' as const,
    message: 'Invalid phone number'
  },
  
  required: {
    required: true,
    message: 'This field is required'
  },
  
  positiveNumber: {
    type: 'number' as const,
    min: 0,
    message: 'Must be a positive number'
  },
  
  currency: {
    type: 'number' as const,
    min: 0,
    message: 'Must be a valid currency amount'
  },
  
  date: {
    type: 'date' as const,
    message: 'Must be a valid date'
  },
  
  zip: {
    type: 'string' as const,
    pattern: /^\d{5}(-\d{4})?$/,
    message: 'Must be a valid ZIP code'
  }
};
